import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface ViewAgentPromptModalProps {
  open: boolean;
  onClose: () => void;
}

const JsonDisplay: React.FC<{ value: any }> = ({ value }) => (
  <Box
    sx={{
      backgroundColor: '#1e1e1e',
      color: '#d4d4d4',
      fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
      fontSize: '0.85rem',
      padding: 2,
      borderRadius: 1,
      overflow: 'auto',
      maxHeight: 300,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}
  >
    {value != null ? JSON.stringify(value, null, 2) : 'null'}
  </Box>
);

const ViewAgentPromptModal: React.FC<ViewAgentPromptModalProps> = ({ open, onClose }) => {
  const { selectedPrompt, loading } = useAppSelector((state) => state.agentPrompts);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalle del Prompt</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : selectedPrompt ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Dominio de Negocio
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedPrompt.businessDomain}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Rol del Sistema
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedPrompt.systemRole}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tarea Principal
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedPrompt.mainTask}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Instrucciones
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedPrompt.instructions || '—'}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Restricciones
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedPrompt.restrictions || '—'}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Reglas de Formato de Respuesta
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedPrompt.responseFormatRules || '—'}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Entrada del Prompt
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedPrompt.promptInput || '—'}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Variables de Contexto
              </Typography>
              <JsonDisplay value={selectedPrompt.contextVariables} />
              <Divider sx={{ mt: 2 }} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Ejemplos (Few-Shot)
              </Typography>
              <JsonDisplay value={selectedPrompt.fewShotExamples} />
              <Divider sx={{ mt: 2 }} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Estado
              </Typography>
              <Chip
                label={selectedPrompt.isActive ? 'Activo' : 'Inactivo'}
                color={selectedPrompt.isActive ? 'success' : 'default'}
                size="small"
                sx={{ mt: 0.5 }}
              />
              <Divider sx={{ mt: 2 }} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Fecha de Creación
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedPrompt.createdAt}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Creado Por
              </Typography>
              <Typography variant="body1">
                {selectedPrompt.createdBy || '—'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography>No hay prompt seleccionado</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAgentPromptModal;
